import { useMemo, HTMLProps } from "react";
import {
  Button,
  Theme,
  DeprecatedTooltip,
  Select,
  Icon,
  Checkbox,
  CheckboxState,
} from "@landtechnologies/components";
import { CSVLink } from "react-csv";
import {
  CalculatorResultTypes,
  CorePriceModes,
  PriceModes,
  RecordType,
  ZooplaComparableDTO,
  ZooplaPriceModes,
} from "react-migration/domains/comparables/typings";
import { getHeadersFactory, getTransformer } from "react-migration/domains/comparables/util";
import { logEvent, useTranslation } from "react-migration/lib";
import {
  getMeasurementSystemPreference,
  hasBetaFeature,
  hasFeature,
} from "src/js/stores/user/actions";
import { dateIsoString } from "src/js/util/dates";
import { Feature } from "src/js/stores/user";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";

export interface ActionsBaseProps extends HTMLProps<HTMLDivElement> {
  loading: boolean;
  priceMode: PriceModes;
  transactions: RecordType[] | ZooplaComparableDTO[];
  incNoFloorArea: boolean;
  setIncNoFloorArea: (calcIncNoFloorArea: boolean) => void;
  includedResults: CalculatorResultTypes;
  setIncludedResults: (val: CalculatorResultTypes) => void;
}

export interface ZooplaActionsProps
  extends Omit<ActionsBaseProps, "transactions" | "isZooplaData"> {
  transactions: ZooplaComparableDTO[];
  priceMode: ZooplaPriceModes;
}

export interface LandTechActionsProps
  extends Omit<ActionsBaseProps, "transactions" | "isZooplaData"> {
  transactions: RecordType[];
  priceMode: CorePriceModes;
}

export type ActionsProps = ActionsBaseProps | ZooplaActionsProps | LandTechActionsProps;
export function Actions(props: ZooplaActionsProps): JSX.Element;
export function Actions(props: LandTechActionsProps): JSX.Element;
export function Actions(props: ActionsBaseProps): JSX.Element;
export function Actions({
  loading,
  transactions,
  priceMode,
  incNoFloorArea,
  setIncNoFloorArea,
  includedResults,
  setIncludedResults,
}: ActionsProps): JSX.Element {
  const { t } = useTranslation();
  const isZooplaData = isZoopla(priceMode);
  const unitPreference = getMeasurementSystemPreference();

  const csvLinkHeaders = useMemo(() => {
    return getHeadersFactory(priceMode)({ t, unitPreference });
  }, [t, unitPreference, priceMode]);

  const csvLinkData = useMemo(() => {
    const transformer = getTransformer(priceMode);
    const data = transformer({
      t,
      unitPreference,
      // @ts-expect-error transformer is polymorphic and was too much of headache to type correctly
      transactions,
    });

    return data;
  }, [t, transactions, unitPreference, priceMode]);

  const hasExportFeature = !isZooplaData || hasFeature(Feature.zooplaCompsCalculatorExport);
  const message = () => {
    if (!hasExportFeature) {
      return t("comparables.calculator.download.requires_license_upgrade");
    } else if (loading) {
      return t("comparables.calculator.results.messages.calculating");
    } else if (transactions.length === 0) {
      return t("comparables.calculator.results.messages.none");
    } else {
      return "";
    }
  };

  const CSVDownload = () => {
    logEvent("Comparables Download CSV", {
      includeNoFloorArea: incNoFloorArea,
      includedResults,
      hometrack: isZooplaData,
      priceMode,
    });
  };

  return (
    <div className="atlas-flex atlas-items-center" data-testid="calc-actions">
      {csvLinkData.length && hasExportFeature ? (
        <CSVLink
          data={csvLinkData}
          headers={csvLinkHeaders}
          filename={`LandInsight Comparables Report (${dateIsoString(Date.now())}).csv`}
        >
          <Button.Primary
            size="large"
            onClick={() => CSVDownload()}
            className="atlas-text-xs sm:atlas-text-sm"
          >
            <i className="icon-lt-download-2-line atlas-text-lg atlas-mr-2" />
            {t("comparables.calculator.download")}
          </Button.Primary>
        </CSVLink>
      ) : (
        <DeprecatedTooltip theme={Theme.Dark} placement="top" description={message()}>
          <Button.Primary
            size="large"
            className="atlas-text-xs sm:atlas-text-sm"
            theme={Theme.Dark}
            disabled
          >
            <i className="icon-lt-download-2-line atlas-text-lg atlas-mr-2" />
            {t("comparables.calculator.download")}
          </Button.Primary>
        </DeprecatedTooltip>
      )}
      <label className="atlas-flex atlas-items-center atlas-text-sm atlas-text-content-inverse-primary atlas-ml-2">
        {hasBetaFeature(Feature.compsCalcDownloadFilter) && (
          <Select
            value={includedResults}
            name="includedResults"
            onValueChange={(v) => {
              setIncludedResults(v as CalculatorResultTypes);
            }}
          >
            <Select.Trigger className="atlas-flex atlas-justify-between atlas-gap-2 atlas-items-center">
              <h4 className="atlas-text-sm atlas-font-medium atlas-my-auto atlas-flex-1 atlas-text-left">
                {t("comparables.calculator.results.result_type")}
              </h4>
              <div className="atlas-border atlas-border-solid atlas-border-neutral-300 hover:atlas-border-blue-600 atlas-rounded-md atlas-cursor-pointer atlas-px-2 atlas-w-32">
                <Select.Value />
                <Select.Icon>
                  <Icon
                    size="sm"
                    icon="icon-lt-arrow-down-s-line"
                    extraClasses="atlas-pl-1 atlas-font-semibold"
                  />
                </Select.Icon>
              </div>
            </Select.Trigger>
            <Select.Content
              side="right"
              avoidCollisions={false}
              position="popper"
              className="atlas-z-30 atlas-text-content-primary"
              alignOffset={-60}
            >
              <Select.Item className="atlas-p-1" value={CalculatorResultTypes.ALL}>
                <Select.ItemText>
                  {t("comparables.calculator.results.result_type.all")}
                </Select.ItemText>
              </Select.Item>
              <Select.Item className="atlas-p-1" value={CalculatorResultTypes.SOLD}>
                <Select.ItemText>
                  {isZooplaData
                    ? t("comparables.calculator.results.result_type.value")
                    : t("comparables.calculator.results.result_type.sold")}
                </Select.ItemText>
              </Select.Item>
              <Select.Item className="atlas-p-1" value={CalculatorResultTypes.MARKET}>
                <Select.ItemText>
                  {t("comparables.calculator.results.result_type.market")}
                </Select.ItemText>
              </Select.Item>
            </Select.Content>
          </Select>
        )}
        {!hasBetaFeature(Feature.compsCalcDownloadFilter) && (
          <>
            <Checkbox
              id="includeFloorArea"
              value={incNoFloorArea ? CheckboxState.CHECKED : CheckboxState.EMPTY}
              onChange={() => setIncNoFloorArea(!incNoFloorArea)}
            />
            <span className="atlas-pl-1 atlas-text-xs sm:atlas-text-sm">
              {t("comparables.calculator.results.floorarea")}
            </span>
          </>
        )}
      </label>
    </div>
  );
}
